/**
 * User roles in the AI Registry system
 */
export enum UserRole {
  END_USER = 'AI-Registry-Users',
  AI_CHAMPION = 'AI-Registry-Champions',
  AI_COUNCIL = 'AI-Registry-Council',
  ADMIN = 'AI-Registry-Admins',
  EXECUTIVE_SPONSOR = 'AI-Registry-Executive-Sponsors',
  IT_TEAM = 'AI-Registry-IT-Team',
}

/**
 * User reference embedded in documents
 */
export interface IUserReference {
  userId: string;
  email: string;
  name: string;
  department?: string;
}

/**
 * Authenticated user from MSAL with roles
 */
export interface IAuthenticatedUser {
  userId: string;
  email: string;
  name: string;
  tenantId: string;
  roles: UserRole[];
}

/**
 * User profile stored in database
 */
export interface IUserProfile {
  id: string;
  tenantId: string;
  email: string;
  displayName: string;
  department?: string;
  region?: string;
  jobTitle?: string;
  roles: UserRole[];
  isActive: boolean;
  preferences: IUserPreferences;
  stats: IUserStats;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * User preferences
 */
export interface IUserPreferences {
  emailNotifications: boolean;
  digestFrequency: 'daily' | 'weekly' | 'never';
  theme?: 'light' | 'dark' | 'system';
}

/**
 * User statistics
 */
export interface IUserStats {
  useCasesSubmitted: number;
  useCasesApproved: number;
  useCasesInUse: number;
  reviewsCompleted?: number; // For champions/council
  avgReviewTimeHours?: number; // For champions/council
}

/**
 * Check if user has any of the specified roles
 */
export function hasRole(user: IAuthenticatedUser | null, roles: UserRole[]): boolean {
  if (!user) return false;
  return user.roles.some((role) => roles.includes(role));
}

/**
 * Check if user is at least a champion (champion, council, or admin)
 */
export function isReviewer(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.AI_CHAMPION, UserRole.AI_COUNCIL, UserRole.ADMIN]);
}

/**
 * Check if user can approve high-risk use cases
 */
export function canApproveHighRisk(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.AI_COUNCIL, UserRole.ADMIN]);
}

/**
 * Check if user is admin
 */
export function isAdmin(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.ADMIN]);
}

/**
 * Check if user is an Executive Sponsor
 */
export function isExecutiveSponsor(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.EXECUTIVE_SPONSOR]);
}

/**
 * Check if user is IT Team member
 */
export function isITTeam(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.IT_TEAM]);
}

/**
 * Check if user can review use cases (AI Council)
 */
export function canReviewUseCases(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.AI_COUNCIL, UserRole.ADMIN]);
}

/**
 * Check if user can provide final approval for use cases (Executive Sponsors)
 */
export function canFinalApproveUseCases(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.EXECUTIVE_SPONSOR, UserRole.ADMIN]);
}

/**
 * Check if user can review AI tools (IT Team)
 */
export function canReviewTools(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.IT_TEAM, UserRole.ADMIN]);
}

/**
 * Check if user can provide final approval for AI tools (Executive Sponsors)
 */
export function canFinalApproveTools(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.EXECUTIVE_SPONSOR, UserRole.ADMIN]);
}

/**
 * Check if user can approve Tier 2 (Champion-level) use cases
 */
export function canChampionApproveUseCases(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [UserRole.AI_CHAMPION, UserRole.ADMIN]);
}

/**
 * Check if user can submit use cases (all except Users)
 */
export function canSubmitUseCases(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [
    UserRole.AI_CHAMPION,
    UserRole.AI_COUNCIL,
    UserRole.IT_TEAM,
    UserRole.EXECUTIVE_SPONSOR,
    UserRole.ADMIN,
  ]);
}

/**
 * Check if user can submit AI tools (all except Users)
 */
export function canSubmitTools(user: IAuthenticatedUser | null): boolean {
  return hasRole(user, [
    UserRole.AI_CHAMPION,
    UserRole.AI_COUNCIL,
    UserRole.IT_TEAM,
    UserRole.EXECUTIVE_SPONSOR,
    UserRole.ADMIN,
  ]);
}



